import { useEffect, useRef, useState } from "react";
import useOnClickOutside from "@/shared/hooks/useOnClickOutside";
import usePosOrderCurrentAction from "@/shared/atoms/usePosOrderCurrentAction";
import Center from "../Base/Center";
import Toggle from "../Toggle/Toggle";
import useToggleNumPad from "@/shared/atoms/useToggleNumPad";

const NumPadItemWrapper = ({
  $noHoverEffect,
  width = undefined,
  flexDirection = undefined,
  alignItems = undefined,
  p = undefined,
  className = "",
  style = undefined,
  ...props
}: any) => (
  <div
    className={`inline-flex max-h-20 items-center justify-center rounded-2xl border border-border bg-white text-small font-semibold shadow-[0_2px_4px_rgba(0,0,0,0.01)] md:text-[2.2rem] ${$noHoverEffect ? "" : "cursor-pointer hover:scale-95 hover:bg-table-blue hover:shadow-[0_3px_6px_rgba(0,0,0,0.15)]"} ${className}`.trim()}
    style={{
      pointerEvents: $noHoverEffect ? "none" : "auto",
      minHeight: "2.75rem",
      width,
      flexDirection,
      alignItems,
      padding: p,
      ...style,
    }}
    {...props}
  />
);

const NumPadItem = ({ children, $noHoverEffect = false, ...props }) => {
  return (
    <NumPadItemWrapper $noHoverEffect={$noHoverEffect} {...props}>
      {children}
    </NumPadItemWrapper>
  );
};

interface NumPadProps {
  onExit?: (value: string) => void;
  show: boolean;
  fullWidth?: boolean;
  callAction?: boolean;
  canHide?: boolean;
}

const actionNameForAutoToggle = ["tip", "deliveryCost", "changeProductPrice"];

const NumPad = ({
  onExit,
  show,
  fullWidth,
  callAction = false,
  canHide = false,
}: NumPadProps) => {
  const [input1, setInput1] = useState<string>("");
  const [input2, setInput2] = useState<string>("");
  const [operator, setOperator] = useState<string | null>(null);
  const [result, setResult] = useState<number | null>(null);
  const ref = useRef();
  const [currentAction] = usePosOrderCurrentAction();
  const [toggleNumPad, setToggleNumPad] = useToggleNumPad();

  useEffect(() => {
    if (actionNameForAutoToggle.includes(currentAction?.name)) {
      setToggleNumPad(true);
    }
  }, [currentAction?.name]);

  const calculate = (val1: number, val2: number, op: string): number => {
    switch (op) {
      case "+":
        return val1 + val2;
      case "-":
        return val1 - val2;
      case "X":
        return val1 * val2;
      case "/":
        return val2 !== 0 ? val1 / val2 : 0;
      default:
        return val2;
    }
  };

  const getCurrentInput = () => {
    return operator ? input2 : input1;
  };

  const setCurrentInput = (value: string) => {
    if (operator) {
      setInput2(value);
    } else {
      setInput1(value);
    }
  };

  const handleNumber = (num: string) => {
    const current = getCurrentInput();
    const newValue = current === "" || current === "0" ? num : current + num;
    setCurrentInput(newValue);
    setResult(null);
  };

  const handleDecimal = () => {
    const current = getCurrentInput();
    if (current === "") {
      setCurrentInput("0.");
    } else if (current.indexOf(".") === -1) {
      setCurrentInput(current + ".");
    }
    setResult(null);
  };

  const handleOperator = (nextOperator: string) => {
    if (input1 && input2 && operator) {
      const val1 = parseFloat(input1) || 0;
      const val2 = parseFloat(input2) || 0;
      const newResult = calculate(val1, val2, operator);
      setInput1(String(newResult));
      setInput2("");
      setResult(newResult);
    } else if (input1 && !input2) {
      setResult(null);
    }
    setOperator(nextOperator);
  };

  const handleEquals = () => {
    if (input1 && input2 && operator) {
      const val1 = parseFloat(input1) || 0;
      const val2 = parseFloat(input2) || 0;
      const newResult = calculate(val1, val2, operator);
      setResult(newResult);
    }
  };

  const handleClear = () => {
    setInput1("");
    setInput2("");
    setOperator(null);
    setResult(null);
  };

  const handleBackspace = () => {
    const current = getCurrentInput();
    if (current.length > 1) {
      let newValue = current.slice(0, -1);
      const isLastDot = newValue[newValue.length - 1] === ".";
      newValue = isLastDot ? newValue.slice(0, -1) : newValue;
      setCurrentInput(newValue);
    } else {
      setCurrentInput("");
    }
    setResult(null);
  };

  const getDisplayValue = () => {
    if (result !== null) {
      return result;
    }
    const current = getCurrentInput();
    return current ? parseFloat(current) : "_";
  };

  useOnClickOutside(ref, () => {
    if (show) {
      const finalValue = result !== null ? String(result) : getCurrentInput();
      handleClear();
      onExit?.(finalValue);
    }
  });

  return (
    <div
      className="relative my-1"
      style={{
        height: toggleNumPad ? "25rem" : "0",
        transition: "height 0.75s ease",
      }}
    >
      {canHide && (
        <Center className="pointer-events-auto absolute right-1/2 -top-28 z-100">
          <Toggle
            onClick={() => {
              setToggleNumPad(!toggleNumPad);
            }}
            active={toggleNumPad}
            display="inline-flex"
            chevronDirection="down"
          />
        </Center>
      )}
      <div
        ref={ref}
        aria-hidden={!toggleNumPad}
        className="relative mb-2 flex flex-col gap-2 rounded-lg p-2"
        style={{
          zIndex: toggleNumPad ? 1 : -1,
          background: "var(--white)",
          maxWidth: fullWidth ? "100%" : "35rem",
          width: fullWidth ? "100%" : "35rem",
          opacity: toggleNumPad ? "1" : "0",
          height: toggleNumPad ? "25rem" : "0",
          overflow: "hidden",
          pointerEvents: toggleNumPad ? "auto" : "none",
          transition: "all 0.25s ease",
        }}
      >
        <div className="grid grid-cols-2 gap-2 flex-[1]">
          <NumPadItem
            $noHoverEffect={true}
            width="100%"
            flexDirection="column"
            alignItems="flex-start"
            p="1rem"
          >
            {input1 && operator && (
              <div className="text-[1.3rem] text-[var(--text-secondary)]">
                {parseFloat(input1) || 0}
                {operator && ` ${operator} `}
                {input2 && (parseFloat(input2) || 0)}
              </div>
            )}
            <div
              className="text-[1.8rem] font-bold text-center w-full"
              style={{ textDecoration: "underline" }}
            >
              {getDisplayValue()}
            </div>
          </NumPadItem>
          <div className="grid grid-cols-[repeat(2,_1fr)] gap-[0.5rem]">
            <NumPadItem
              onClick={(e) => {
                e.stopPropagation();
                const finalValue =
                  result !== null ? String(result) : getCurrentInput();
                onExit?.(finalValue);
                callAction && currentAction.action?.(finalValue);
                handleClear();
              }}
            >
              Enter
            </NumPadItem>
            <NumPadItem
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
            >
              C
            </NumPadItem>
          </div>
          {/*  <NumPadItem
          onClick={(e) => {
            e.stopPropagation();
            handleOperator("/");
          }}
        >
          /
        </NumPadItem> */}
        </div>

        <div className="grid grid-cols-[repeat(4,_1fr)] gap-[0.5rem] flex-[1]">
          <NumPadItem
            onClick={(e) => {
              e.stopPropagation();
              handleNumber("1");
            }}
          >
            1
          </NumPadItem>
          <NumPadItem
            onClick={(e) => {
              e.stopPropagation();
              handleNumber("2");
            }}
          >
            2
          </NumPadItem>
          <NumPadItem
            onClick={(e) => {
              e.stopPropagation();
              handleNumber("3");
            }}
          >
            3
          </NumPadItem>
          <NumPadItem
            onClick={(e) => {
              e.stopPropagation();
              handleOperator("X");
            }}
          >
            X
          </NumPadItem>
        </div>

        <div className="grid grid-cols-[repeat(4,_1fr)] gap-[0.5rem] flex-[1]">
          <NumPadItem
            onClick={(e) => {
              e.stopPropagation();
              handleNumber("4");
            }}
          >
            4
          </NumPadItem>
          <NumPadItem
            onClick={(e) => {
              e.stopPropagation();
              handleNumber("5");
            }}
          >
            5
          </NumPadItem>
          <NumPadItem
            onClick={(e) => {
              e.stopPropagation();
              handleNumber("6");
            }}
          >
            6
          </NumPadItem>
          <NumPadItem
            onClick={(e) => {
              e.stopPropagation();
              handleOperator("-");
            }}
          >
            -
          </NumPadItem>
        </div>

        <div className="grid grid-cols-[repeat(4,_1fr)] gap-[0.5rem] flex-[1]">
          <NumPadItem
            onClick={(e) => {
              e.stopPropagation();
              handleNumber("7");
            }}
          >
            7
          </NumPadItem>
          <NumPadItem
            onClick={(e) => {
              e.stopPropagation();
              handleNumber("8");
            }}
          >
            8
          </NumPadItem>
          <NumPadItem
            onClick={(e) => {
              e.stopPropagation();
              handleNumber("9");
            }}
          >
            9
          </NumPadItem>
          <NumPadItem
            onClick={(e) => {
              e.stopPropagation();
              handleOperator("+");
            }}
          >
            +
          </NumPadItem>
        </div>

        <div className="grid grid-cols-[repeat(4,_1fr)] gap-[0.5rem] flex-[1]">
          <NumPadItem
            onClick={(e) => {
              e.stopPropagation();
              handleDecimal();
            }}
          >
            ,
          </NumPadItem>
          <NumPadItem
            onClick={(e) => {
              e.stopPropagation();
              handleNumber("0");
            }}
          >
            0
          </NumPadItem>
          <NumPadItem
            onClick={(e) => {
              e.stopPropagation();
              handleBackspace();
            }}
          >
            &lt;
          </NumPadItem>
          <NumPadItem
            onClick={(e) => {
              e.stopPropagation();
              handleEquals();
            }}
          >
            =
          </NumPadItem>
        </div>
      </div>
    </div>
  );
};

export default NumPad;
